import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { type ConcertCalendarDto } from '../api';

const MONTH_LIST = [
  { key: '2026-08', label: 'สิงหาคม 2026', year: 2026, month: 8 },
  { key: '2026-09', label: 'กันยายน 2026', year: 2026, month: 9 },
  { key: '2026-10', label: 'ตุลาคม 2026', year: 2026, month: 10 },
  { key: '2026-11', label: 'พฤศจิกายน 2026', year: 2026, month: 11 },
  { key: '2026-12', label: 'ธันวาคม 2026', year: 2026, month: 12 },
  { key: '2027-01', label: 'มกราคม 2027', year: 2027, month: 1 },
];

const WEEKDAYS = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];

// Grid cells are fixed-height so the layout never reflows regardless of how many
// events land on a day: each cell shows up to MAX_ROWS single-line truncated pills
// plus a "+N more" toggle; full details surface on hover (tooltip) or click (modal).
const CELL_H = 110;
const ROW_H = 19;
const MAX_ROWS = 4;
const ellipsis: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };

function provinceStyle(province: string): { border: string; color: string } {
  if (province.includes('กรุงเทพ')) return { border: '#ff9a3d', color: '#ff9a3d' };
  if (province.includes('นนทบุรี')) return { border: '#10b981', color: '#34d399' };
  if (province.includes('เชียงใหม่')) return { border: '#0ea5e9', color: '#38bdf8' };
  if (province.includes('ชลบุรี')) return { border: '#a855f7', color: '#c084fc' };
  if (province.includes('ภูเก็ต')) return { border: '#f43f5e', color: '#fb7185' };
  return { border: '#14b8a6', color: '#2dd4bf' };
}

function parseDaysForEvent(dateLabel: string, sortDate: string): number[] {
  const matchRange = dateLabel.match(/^(\d+)\s*-\s*(\d+)/);
  if (matchRange) {
    const start = parseInt(matchRange[1], 10);
    const end = parseInt(matchRange[2], 10);
    const days: number[] = [];
    for (let d = start; d <= end; d++) {
      days.push(d);
    }
    return days;
  }
  const matchSingle = dateLabel.match(/^(\d+)/);
  if (matchSingle) {
    return [parseInt(matchSingle[1], 10)];
  }
  const sortDayMatch = sortDate.match(/-(\d{2})$/);
  if (sortDayMatch) {
    return [parseInt(sortDayMatch[1], 10)];
  }
  return [1];
}

function EventRow({ ev, onOpen }: { ev: ConcertCalendarDto; onOpen: (ev: ConcertCalendarDto) => void }) {
  const s = provinceStyle(ev.province);
  const ref = useRef<HTMLDivElement | null>(null);
  const [tip, setTip] = useState<{ left: number; top: number; transform: string; width: number } | null>(null);

  // Cells are overflow:hidden (that's what keeps the grid stable), so the hover card
  // is portalled to <body> and positioned from the row's viewport rect instead.
  const show = () => {
    const r = ref.current!.getBoundingClientRect();
    const W = 264;
    const above = r.top > 150;
    setTip({
      left: Math.min(Math.max(8, r.left), window.innerWidth - W - 8),
      top: above ? r.top - 8 : r.bottom + 8,
      transform: above ? 'translateY(-100%)' : 'none',
      width: W,
    });
  };

  return (
    <div
      ref={ref}
      className="cal-pill"
      onClick={() => onOpen(ev)}
      onMouseOver={show}
      onMouseOut={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setTip(null);
      }}
      style={{ display: 'flex', alignItems: 'center', gap: 5, height: ROW_H, padding: '0 4px', borderRadius: 3, cursor: 'pointer', minWidth: 0 }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 2, background: s.color, flex: 'none' }} />
      <span style={{ ...ellipsis, fontSize: 11, color: 'var(--fg)' }}>
        {ev.eventName}
        <span style={{ color: 'var(--muted)' }}> · {ev.artist}</span>
      </span>
      {tip &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              boxSizing: 'border-box',
              left: tip.left,
              top: tip.top,
              transform: tip.transform,
              width: tip.width,
              background: '#0d0d0d',
              border: '1px solid #ff9a3d',
              borderRadius: 8,
              padding: '12px 14px',
              fontSize: 12,
              lineHeight: 1.5,
              zIndex: 900,
              pointerEvents: 'none',
              boxShadow: '0 16px 32px rgba(0,0,0,.55)',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--fg)', marginBottom: 4 }}>{ev.eventName}</div>
            <div style={{ color: s.color, fontWeight: 600, marginBottom: 6 }}>🎤 {ev.artist}</div>
            <div style={{ color: 'var(--muted)' }}>🏛️ {ev.venue}</div>
            <div style={{ color: 'var(--muted)', marginTop: 2 }}>📍 {ev.province}</div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function DayCell({
  dayNum,
  events,
  isWeekend,
  onOpen,
  onMore,
}: {
  dayNum: number;
  events: ConcertCalendarDto[];
  isWeekend: boolean;
  onOpen: (ev: ConcertCalendarDto) => void;
  onMore: (day: number, events: ConcertCalendarDto[]) => void;
}) {
  const shown = events.slice(0, MAX_ROWS);
  const extra = events.length - shown.length;
  return (
    <div
      style={{
        height: CELL_H,
        boxSizing: 'border-box',
        padding: '5px 4px',
        background: isWeekend ? 'rgba(255,255,255,.015)' : 'transparent',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, fontFamily: 'var(--mono)', color: isWeekend ? '#ff9a3d' : 'var(--muted)', padding: '0 3px 3px' }}>
        {dayNum}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {shown.map((ev) => (
          <EventRow key={ev.id} ev={ev} onOpen={onOpen} />
        ))}
        {extra > 0 && (
          <button
            type="button"
            onClick={() => onMore(dayNum, events)}
            style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 10.5, fontWeight: 500, padding: '2px 4px', cursor: 'pointer', textAlign: 'left', height: ROW_H }}
          >
            + {extra} เพิ่มเติม
          </button>
        )}
      </div>
    </div>
  );
}

function DayListModal({
  day,
  events,
  onClose,
  onOpen,
}: {
  day: number;
  events: ConcertCalendarDto[];
  onClose: () => void;
  onOpen: (ev: ConcertCalendarDto) => void;
}) {
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', backdropFilter: 'blur(6px)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--panel)', border: '1px solid var(--panel-border)', borderRadius: 12, padding: 26, maxWidth: 460, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
            วันที่ {day} · {events.length} งาน
          </h2>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>
            ✕
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {events.map((ev) => {
            const s = provinceStyle(ev.province);
            return (
              <div
                key={ev.id}
                onClick={() => onOpen(ev)}
                style={{ background: 'var(--bg)', border: `1px solid ${s.border}`, borderRadius: 6, padding: '10px 12px', cursor: 'pointer' }}
              >
                <div style={{ fontWeight: 700, color: s.color, fontSize: 13 }}>{ev.artist}</div>
                <div style={{ fontSize: 12.5, marginTop: 2 }}>{ev.eventName}</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
                  📍 {ev.province} · 🏛️ {ev.venue}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Internal date-clash reference for event organizers — not a public discovery page.
 * `embedded` drops the standalone header/footer chrome for use as an Admin/SuperAdmin tab;
 * `fetchData` lets callers point at their own authenticated endpoint (admin vs superadmin).
 */
export default function ConcertCalendar({ embedded = false, fetchData }: { embedded?: boolean; fetchData: () => Promise<ConcertCalendarDto[]> }) {
  const [items, setItems] = useState<ConcertCalendarDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedMonthIdx, setSelectedMonthIdx] = useState(0);
  const [selectedProvince, setSelectedProvince] = useState('all');
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [activeModalEvent, setActiveModalEvent] = useState<ConcertCalendarDto | null>(null);
  const [dayModal, setDayModal] = useState<{ day: number; events: ConcertCalendarDto[] } | null>(null);

  useEffect(() => {
    if (!embedded) document.title = 'ปฏิทินคอนเสิร์ต & เฟสติวัล 2026–2027 — JamTick';
    fetchData()
      .then((data) => {
        setItems(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[concert-calendar] failed to fetch data', err);
        setError('ไม่สามารถโหลดข้อมูลปฏิทินคอนเสิร์ตได้');
        setLoading(false);
      });
  }, [embedded, fetchData]);

  const currentMonthInfo = MONTH_LIST[selectedMonthIdx];
  const provinces = useMemo(() => Array.from(new Set(items.map((i) => i.province))).filter(Boolean), [items]);

  const filtered = items.filter((item) => {
    const matchSearch =
      item.eventName.toLowerCase().includes(search.toLowerCase()) ||
      item.artist.toLowerCase().includes(search.toLowerCase()) ||
      item.venue.toLowerCase().includes(search.toLowerCase()) ||
      item.province.toLowerCase().includes(search.toLowerCase()) ||
      item.dateLabel.toLowerCase().includes(search.toLowerCase());

    if (!matchSearch) return false;
    if (selectedProvince !== 'all' && item.province !== selectedProvince) return false;
    return true;
  });

  const year = currentMonthInfo.year;
  const month = currentMonthInfo.month; // 1-indexed
  const firstDayWeekday = new Date(year, month - 1, 1).getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month, 0).getDate();

  const prevMonthDays = Array.from({ length: firstDayWeekday }, (_, i) => i);
  const monthDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const getEventsOnDay = (dayNum: number) => {
    return filtered.filter((item) => {
      if (item.monthKey !== currentMonthInfo.key) return false;
      const days = parseDaysForEvent(item.dateLabel, item.sortDate);
      return days.includes(dayNum);
    });
  };

  return (
    <div className="page concert-calendar-page" style={embedded ? undefined : { minHeight: '100vh', background: 'var(--bg)', color: 'var(--fg)' }}>
      {!embedded && (
        <header className="site-header shell sticky" style={{ borderBottom: '1px solid var(--line)', background: 'rgba(18, 18, 18, 0.9)', backdropFilter: 'blur(12px)' }}>
          <div className="container" style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
            <Link to="/" className="logo" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
              <span style={{ fontWeight: 800, fontSize: 20, color: 'var(--fg)', letterSpacing: '0.05em' }}>JAMTICK</span>
              <span className="mono-tag" style={{ fontSize: 10, padding: '3px 8px', borderColor: '#ff9a3d', color: '#ff9a3d' }}>
                CALENDAR
              </span>
            </Link>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="shell container" style={{ padding: embedded ? 0 : '36px 20px 80px' }}>
        {!embedded && (
          <div style={{ textAlign: 'center', maxWidth: 800, margin: '0 auto 36px' }}>
            <span className="mono-tag" style={{ borderColor: '#ff9a3d', color: '#ff9a3d', fontSize: 11, padding: '4px 14px', marginBottom: 14, display: 'inline-block' }}>
              Interactive Concert & Festival Calendar
            </span>
            <h1 style={{ fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 800, margin: '8px 0 12px', lineHeight: 1.2 }}>ปฏิทินคอนเสิร์ต & เทศกาลดนตรี</h1>
            <p style={{ color: 'var(--muted)', fontSize: 14.5, lineHeight: 1.6, margin: 0 }}>
              ดูตารางงานคอนเสิร์ตเป็นรูปแบบปฏิทินประจำเดือน วางเมาส์บนงานเพื่อดูรายละเอียด หรือคลิกเพื่อดูข้อมูลเต็ม
            </p>
          </div>
        )}

        {/* Top Controls Bar */}
        <div style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 16, padding: 20, marginBottom: 28 }}>
          {/* Search + Province + View Mode */}
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ค้นหาชื่องาน, ศิลปิน, สถานที่จัดงาน หรือจังหวัด..."
              style={{
                flex: '1 1 260px',
                minWidth: 0,
                padding: '11px 14px',
                borderRadius: 6,
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                color: 'var(--fg)',
                fontSize: 13.5,
              }}
            />
            <select
              value={selectedProvince}
              onChange={(e) => setSelectedProvince(e.target.value)}
              style={{ padding: '11px 14px', borderRadius: 6, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13, cursor: 'pointer' }}
            >
              <option value="all">จังหวัดทั้งหมด</option>
              {provinces.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 3 }}>
              <button
                type="button"
                onClick={() => setViewMode('calendar')}
                style={{
                  padding: '7px 16px',
                  borderRadius: 4,
                  fontSize: 12.5,
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  background: viewMode === 'calendar' ? '#ff9a3d' : 'transparent',
                  color: viewMode === 'calendar' ? '#171717' : 'var(--muted)',
                }}
              >
                ปฏิทิน (Grid)
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                style={{
                  padding: '7px 16px',
                  borderRadius: 4,
                  fontSize: 12.5,
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  background: viewMode === 'list' ? '#ff9a3d' : 'transparent',
                  color: viewMode === 'list' ? '#171717' : 'var(--muted)',
                }}
              >
                รายการ (List)
              </button>
            </div>
          </div>

          {/* Month Selector Pills */}
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
            {MONTH_LIST.map((m, idx) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setSelectedMonthIdx(idx)}
                style={{
                  fontSize: 12.5,
                  padding: '6px 16px',
                  borderRadius: 20,
                  whiteSpace: 'nowrap',
                  border: '1px solid',
                  cursor: 'pointer',
                  background: selectedMonthIdx === idx ? '#ff9a3d' : 'transparent',
                  color: selectedMonthIdx === idx ? '#171717' : 'var(--fg)',
                  borderColor: selectedMonthIdx === idx ? '#ff9a3d' : 'var(--border)',
                  fontWeight: selectedMonthIdx === idx ? 700 : 500,
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading / Error */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--muted)' }}>กำลังโหลดปฏิทินคอนเสิร์ต...</div>
        ) : error ? (
          <div className="form-error">{error}</div>
        ) : viewMode === 'calendar' ? (
          <>
            {/* Month Header Nav */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <button
                type="button"
                onClick={() => setSelectedMonthIdx((prev) => Math.max(0, prev - 1))}
                disabled={selectedMonthIdx === 0}
                style={{
                  background: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  color: selectedMonthIdx === 0 ? 'var(--dim)' : 'var(--fg)',
                  padding: '6px 14px',
                  fontSize: 13,
                  cursor: selectedMonthIdx === 0 ? 'default' : 'pointer',
                }}
              >
                ‹ เดือนก่อนหน้า
              </button>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{currentMonthInfo.label}</div>
              <button
                type="button"
                onClick={() => setSelectedMonthIdx((prev) => Math.min(MONTH_LIST.length - 1, prev + 1))}
                disabled={selectedMonthIdx === MONTH_LIST.length - 1}
                style={{
                  background: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  color: selectedMonthIdx === MONTH_LIST.length - 1 ? 'var(--dim)' : 'var(--fg)',
                  padding: '6px 14px',
                  fontSize: 13,
                  cursor: selectedMonthIdx === MONTH_LIST.length - 1 ? 'default' : 'pointer',
                }}
              >
                เดือนถัดไป ›
              </button>
            </div>

            <div style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
              {/* Weekdays Header Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', borderBottom: '1px solid var(--line)', background: 'rgba(0,0,0,0.2)' }}>
                {WEEKDAYS.map((wd, i) => (
                  <div
                    key={wd}
                    style={{
                      padding: '10px 8px',
                      textAlign: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      fontFamily: 'var(--mono)',
                      color: i === 0 || i === 6 ? '#ff9a3d' : 'var(--muted)',
                      borderRight: i < 6 ? '1px solid var(--line)' : 'none',
                    }}
                  >
                    {wd}
                  </div>
                ))}
              </div>

              {/* Calendar Days Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))' }}>
                {prevMonthDays.map((_, pIdx) => (
                  <div
                    key={`prev_${pIdx}`}
                    style={{ height: CELL_H, minWidth: 0, background: 'rgba(0,0,0,0.2)', borderRight: pIdx % 7 < 6 ? '1px solid var(--line)' : 'none', borderBottom: '1px solid var(--line)' }}
                  />
                ))}

                {monthDays.map((dayNum, cellIdx) => {
                  const dayEvents = getEventsOnDay(dayNum);
                  const colIndex = (firstDayWeekday + cellIdx) % 7;
                  const isWeekend = colIndex === 0 || colIndex === 6;

                  return (
                    <div
                      key={`day_${dayNum}`}
                      style={{ minWidth: 0, overflow: 'hidden', borderRight: colIndex < 6 ? '1px solid var(--line)' : 'none', borderBottom: '1px solid var(--line)' }}
                    >
                      <DayCell
                        dayNum={dayNum}
                        events={dayEvents}
                        isWeekend={isWeekend}
                        onOpen={setActiveModalEvent}
                        onMore={(day, evs) => setDayModal({ day, events: evs })}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          /* ============================================================ */
          /* CARD LIST VIEW                                               */
          /* ============================================================ */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
            {filtered.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 20px', background: 'var(--panel)', borderRadius: 16, border: '1px solid var(--line)' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🗓️</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>ไม่พบรายการคอนเสิร์ต</h3>
              </div>
            ) : (
              filtered.map((item) => {
                const style = provinceStyle(item.province);
                return (
                  <div
                    key={item.id}
                    onClick={() => setActiveModalEvent(item)}
                    style={{
                      background: 'var(--panel)',
                      border: '1px solid var(--line)',
                      borderRadius: 16,
                      padding: 22,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, border-color 0.2s ease',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <span className="mono-tag" style={{ borderColor: '#ff9a3d', color: '#ff9a3d', fontSize: 11, padding: '3px 10px' }}>
                          📅 {item.dateLabel}
                        </span>
                        <span style={{ fontSize: 11, color: style.color, border: `1px solid ${style.border}`, padding: '3px 10px', borderRadius: 12, fontWeight: 600, ...ellipsis, maxWidth: 150 }}>
                          📍 {item.province}
                        </span>
                      </div>
                      <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--fg)', margin: '0 0 8px 0', lineHeight: 1.3 }}>{item.eventName}</h3>
                      <div style={{ fontSize: 13.5, color: style.color, fontWeight: 600, marginBottom: 14 }}>🎤 {item.artist}</div>
                    </div>
                    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
                      🏛️ <strong>สถานที่:</strong> {item.venue}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </main>

      {/* Appointment Detail Modal */}
      {activeModalEvent && (
        <div
          onClick={() => setActiveModalEvent(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 20, padding: 28, maxWidth: 480, width: '100%', boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6)', position: 'relative' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span className="mono-tag" style={{ borderColor: '#ff9a3d', color: '#ff9a3d', fontSize: 12, padding: '4px 12px' }}>
                📅 {activeModalEvent.dateLabel}
              </span>
              <button type="button" onClick={() => setActiveModalEvent(null)} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>
                ✕
              </button>
            </div>

            <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--fg)', margin: '0 0 10px', lineHeight: 1.3 }}>{activeModalEvent.eventName}</h2>

            <div style={{ fontSize: 15, color: provinceStyle(activeModalEvent.province).color, fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>🎤</span> ศิลปิน: {activeModalEvent.artist}
            </div>

            <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 16, border: '1px solid var(--border)', display: 'grid', gap: 12, fontSize: 13.5 }}>
              <div>
                <span style={{ color: 'var(--muted)' }}>🏛️ สถานที่จัดงาน:</span>
                <div style={{ fontWeight: 600, color: 'var(--fg)', marginTop: 2 }}>{activeModalEvent.venue}</div>
              </div>
              <div>
                <span style={{ color: 'var(--muted)' }}>📍 จังหวัด:</span>
                <div style={{ fontWeight: 600, color: 'var(--fg)', marginTop: 2 }}>{activeModalEvent.province}</div>
              </div>
            </div>

            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-primary" onClick={() => setActiveModalEvent(null)} style={{ padding: '8px 24px' }}>
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day overflow modal ("+N more") */}
      {dayModal && (
        <DayListModal
          day={dayModal.day}
          events={dayModal.events}
          onClose={() => setDayModal(null)}
          onOpen={(ev) => {
            setDayModal(null);
            setActiveModalEvent(ev);
          }}
        />
      )}

      {!embedded && (
        <footer style={{ borderTop: '1px solid var(--line)', background: 'var(--panel)', padding: '36px 0 48px' }}>
          <div className="shell container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontWeight: 700, letterSpacing: '0.2em', fontSize: 14, color: 'var(--fg)' }}>JAMTICK CALENDAR</div>
              <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 4 }}>ศูนย์รวมตารางงานคอนเสิร์ตทั่วประเทศไทย</div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>© 2026 JamTick. All rights reserved.</div>
          </div>
        </footer>
      )}
    </div>
  );
}
